/**
 * React Query hooks for Groups API
 * Uses TanStack Query for caching, loading states, and error handling
 */

import { useQuery } from '@tanstack/react-query'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { fetchGroups, fetchGroupById, fetchGroupDashboard } from '../helpers/groupsApi'
import { fetchGroupMembers } from '../helpers/groupMembersApi'
import { STALE_TIME_GROUPS_MS } from '../constants/reactQuery.constants'

/**
 * Hook to fetch groups list
 * @param userId - User ID to fetch groups for
 * @param enabled - Whether the query should run (default: true)
 */
export const useGroupsQuery = (userId: string, enabled: boolean = true) => {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: ['groups', userId],
    queryFn: async () => {
      return fetchGroups(userId, apiClient)
    },
    enabled: enabled && !!userId,
    staleTime: STALE_TIME_GROUPS_MS,
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  })
}

/**
 * Hook to fetch a single group by ID. Used for deep linking so the group screen can load without the full list.
 */
export const useGroupByIdQuery = (groupId: string, enabled: boolean = true) => {
  const apiClient = useApiClient()
  return useQuery({
    queryKey: ['groups', 'detail', groupId],
    queryFn: () => fetchGroupById(groupId, apiClient),
    enabled: enabled && !!groupId,
    staleTime: STALE_TIME_GROUPS_MS,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch member profiles for a single group (names, avatars for non-friends).
 * Disabled by default: dashboard already includes members; use this only if you need
 * a separate GET /groups/:id/members (e.g. outside group page). Pass enabled: true to run.
 */
export const useGroupMembersQuery = (groupId: string, enabled: boolean = false) => {
  const apiClient = useApiClient()
  return useQuery({
    queryKey: ['groups', groupId, 'members'],
    queryFn: () => fetchGroupMembers(groupId, apiClient),
    enabled: enabled && !!groupId,
    staleTime: STALE_TIME_GROUPS_MS,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * BFF hook: fetches group info + members + completed challenges in one request.
 * Replaces the combination of useGroupByIdQuery + useGroupMembersQuery + useGroupChallengesForLeaderboardQuery
 * on the group leaderboard screen.
 */
export const useGroupDashboardQuery = (groupId: string, enabled: boolean = true) => {
  const apiClient = useApiClient()
  return useQuery({
    queryKey: ['groups', 'dashboard', groupId],
    queryFn: () => fetchGroupDashboard(groupId, apiClient),
    enabled: enabled && !!groupId,
    staleTime: STALE_TIME_GROUPS_MS,
    gcTime: 10 * 60 * 1000,
  })
}

